import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import { forwardRef, useSyncExternalStore, type ComponentPropsWithoutRef } from "react";
import { cn } from "@/shared/lib/utils";

/**
 * Radix Toast wrapper + a module-level store so any code (including the SSE
 * notifications hook, which fires from outside React's render tree) can pop a
 * toast imperatively via `useToast()`. Mount `<ToastHost />` once near the app
 * root.
 *
 * Surface tiers per design.md §6/§9/§12:
 *   root → glass-surface-strong, rounded-2xl, z-toast, CSS data-state transitions
 *   title → type-heading-sm text-text; description → type-body-base text-text-muted
 */
export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = forwardRef<
  HTMLOListElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(function ToastViewport({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Viewport
      ref={ref}
      className={cn(
        "fixed bottom-0 left-0 z-toast m-0 flex w-full max-w-sm list-none flex-col gap-3 p-4 outline-none",
        className,
      )}
      {...props}
    />
  );
});

export const ToastRoot = forwardRef<
  HTMLLIElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Root>
>(function ToastRoot({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(
        "rounded-2xl glass-surface-strong p-4 pr-10",
        "relative flex flex-col gap-1",
        "transition-all duration-300 ease-out",
        "data-[state=open]:translate-y-0 data-[state=open]:opacity-100",
        "data-[state=closed]:translate-y-2 data-[state=closed]:opacity-0",
        "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
        "data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform",
        "data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
        className,
      )}
      {...props}
    />
  );
});

export const ToastTitle = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(function ToastTitle({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Title
      ref={ref}
      className={cn("type-heading-sm text-text", className)}
      {...props}
    />
  );
});

export const ToastDescription = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(function ToastDescription({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Description
      ref={ref}
      className={cn("type-body-base text-text-muted", className)}
      {...props}
    />
  );
});

export const ToastClose = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(function ToastClose({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Close
      ref={ref}
      aria-label="Dismiss"
      className={cn(
        "absolute right-3 top-3 rounded-lg p-1 text-text-muted",
        "transition-colors hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
        className,
      )}
      {...props}
    >
      <X className="size-4" aria-hidden />
    </ToastPrimitive.Close>
  );
});

// --- Imperative store ------------------------------------------------------

export interface ToastInput {
  title: string;
  description?: string;
}

interface ToastItem extends ToastInput {
  id: string;
}

let items: ToastItem[] = [];
let counter = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

const store = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): ToastItem[] {
    return items;
  },
  push(input: ToastInput): void {
    counter += 1;
    items = [...items, { id: `toast-${counter}`, ...input }];
    emit();
  },
  remove(id: string): void {
    items = items.filter((t) => t.id !== id);
    emit();
  },
};

// Stable across renders so it's safe to list in effect dependency arrays.
const toastApi = {
  toast(input: ToastInput): void {
    store.push(input);
  },
};

/** Imperative toast trigger: `const { toast } = useToast(); toast({ title })`. */
export function useToast(): typeof toastApi {
  return toastApi;
}

/**
 * Renders queued toasts. Mount once near the app root. Each toast manages its
 * own open state + auto-dismiss timer (Radix); on close we drop it from the store.
 */
export function ToastHost() {
  const toasts = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return (
    <ToastProvider swipeDirection="left">
      {toasts.map((t) => (
        <ToastRoot
          key={t.id}
          onOpenChange={(open) => {
            if (!open) store.remove(t.id);
          }}
        >
          <ToastTitle>{t.title}</ToastTitle>
          {t.description ? <ToastDescription>{t.description}</ToastDescription> : null}
          <ToastClose />
        </ToastRoot>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
